/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Table cells config for import personal data
 *
 * @author Julia Ignatieva
 * @version 1.16.0
 * @since 1.16.0
 */

import range from 'lodash/range';

import { formatNumberToPhone } from '../../../../components/common/PhoneMask/formatNumberToPhone';
import { ITableField } from '../../../../components/Table/reducers/tableReducer';
import { EColumnCell } from '../../../../components/Table/types/columnTypes';
import {
    IImportPersonalDataTableStatusTranslation,
    IImportPersonalDataTableTranslation,
} from '../../../../modules/internationalization/types/wizards/importPersonalData';
import { IWizardImportPersonalDataValidatedSubscriberResponse } from '../../types/wizardImportPersonalDataTypes';
import { EImportPersonalDataField } from '../../wizardImportPersonalDataConstant';

export const importPersonalDataColumnsWidth: { [Key in EImportPersonalDataField]: number } = {
    [EImportPersonalDataField.ACTION]: 50,
    [EImportPersonalDataField.MSISDN]: 200,
    [EImportPersonalDataField.SURNAME]: 150,
    [EImportPersonalDataField.NAME]: 150,
    [EImportPersonalDataField.PATRONYMIC]: 150,
    [EImportPersonalDataField.FIO]: 250,
    [EImportPersonalDataField.FIO_FROM_FILE]: 250,
    [EImportPersonalDataField.NATIONALITY_INFO]: 200,
    [EImportPersonalDataField.DOC_TYPE]: 150,
    [EImportPersonalDataField.DOC_SERIES]: 150,
    [EImportPersonalDataField.DOC_NUMBER]: 150,
    [EImportPersonalDataField.DOC_NUMBER_FROM_FILE]: 150,
    [EImportPersonalDataField.DOC_ISSUED_BY]: 150,
    [EImportPersonalDataField.DOC_DEPARTMENT_CODE]: 200,
    [EImportPersonalDataField.DOC_ISSUE_DATE]: 150,
    [EImportPersonalDataField.DOC_VALID_DATE]: 150,
    [EImportPersonalDataField.BIRTH_DATE]: 150,
    [EImportPersonalDataField.BIRTH_PLACE]: 150,
    [EImportPersonalDataField.ADDR_COUNTRY]: 150,
    [EImportPersonalDataField.ADDR_REGION]: 150,
    [EImportPersonalDataField.ADDR_CITY]: 250,
    [EImportPersonalDataField.PLANNING_ELEMENT]: 150,
    [EImportPersonalDataField.ADDR_STREET]: 150,
    [EImportPersonalDataField.ADDR_HOUSE]: 150,
    [EImportPersonalDataField.ADDR_BLOCK]: 150,
    [EImportPersonalDataField.ADDR_STRUCTURE]: 150,
    [EImportPersonalDataField.ADDR_APARTMENT]: 150,
    [EImportPersonalDataField.EMAIL]: 200,
    [EImportPersonalDataField.PIC_STATUS]: 250,
    [EImportPersonalDataField.STATUS]: 300,
};

export const importPersonalDataColumnNamesIfCamEnabled: EImportPersonalDataField[] = [
    EImportPersonalDataField.MSISDN,
    EImportPersonalDataField.SURNAME,
    EImportPersonalDataField.NAME,
    EImportPersonalDataField.PATRONYMIC,
    EImportPersonalDataField.NATIONALITY_INFO,
    EImportPersonalDataField.DOC_TYPE,
    EImportPersonalDataField.DOC_SERIES,
    EImportPersonalDataField.DOC_NUMBER,
    EImportPersonalDataField.DOC_ISSUED_BY,
    EImportPersonalDataField.DOC_DEPARTMENT_CODE,
    EImportPersonalDataField.DOC_ISSUE_DATE,
    EImportPersonalDataField.DOC_VALID_DATE,
    EImportPersonalDataField.BIRTH_DATE,
    EImportPersonalDataField.BIRTH_PLACE,
    EImportPersonalDataField.ADDR_COUNTRY,
    EImportPersonalDataField.ADDR_REGION,
    EImportPersonalDataField.ADDR_CITY,
    EImportPersonalDataField.PLANNING_ELEMENT,
    EImportPersonalDataField.ADDR_STREET,
    EImportPersonalDataField.ADDR_HOUSE,
    EImportPersonalDataField.ADDR_BLOCK,
    EImportPersonalDataField.ADDR_STRUCTURE,
    EImportPersonalDataField.ADDR_APARTMENT,
    EImportPersonalDataField.EMAIL,
];

export const importPersonalDataColumnNamesFinalStep: EImportPersonalDataField[] = [
    EImportPersonalDataField.MSISDN,
    EImportPersonalDataField.FIO,
    EImportPersonalDataField.FIO_FROM_FILE,
    EImportPersonalDataField.DOC_NUMBER,
    EImportPersonalDataField.DOC_NUMBER_FROM_FILE,
    EImportPersonalDataField.PIC_STATUS,
    EImportPersonalDataField.STATUS,
    EImportPersonalDataField.ACTION,
];

export const getColumnFilterType = (): string => {
    return '';
};

export const getColumnCellType = (name: string): string => {
    switch (name) {
        case EImportPersonalDataField.ACTION:
            return EColumnCell.rowDeleteAction;
        case EImportPersonalDataField.STATUS:
            return EColumnCell.importPersonalDataStatus;
        default:
            return EColumnCell.text;
    }
};

export const getColumnFixedType = (name: string): string | boolean => {
    switch (name) {
        // case EImportPersonalDataField.ACTION:
        //     return 'right'
        default:
            return false;
    }
};

export const formImportPersonalDataTableFields = (
    translation: IImportPersonalDataTableTranslation,
): Array<ITableField<EImportPersonalDataField>> => {
    // рычаг отвечает за получение из CAM персональных данных конечного пользователя, связанного с абонентом
    const columnsNames: EImportPersonalDataField[] = importPersonalDataColumnNamesIfCamEnabled;

    return  range(columnsNames.length)
        .map(number => {
            const field = columnsNames[number];
            if (field === EImportPersonalDataField.PLANNING_ELEMENT) {
                return {
                    checked: true,
                    description: translation[field].description,
                    label: translation[field].title,
                    name: field,
                    order: number,
                    resizable: true,
                    size: importPersonalDataColumnsWidth[field],
                    type: 'S',
                } as ITableField<EImportPersonalDataField>
            }
            return {
                checked: true,
                label: translation[field],
                name: field,
                order: number,
                resizable: true,
                size: importPersonalDataColumnsWidth[field],
                type: 'S',
            } as ITableField<EImportPersonalDataField>
    });
}

export const formImportPersonalDataTableFieldsFinalStep = (
    translation: IImportPersonalDataTableTranslation,
): Array<ITableField<EImportPersonalDataField>> => {
    return range(importPersonalDataColumnNamesFinalStep.length)
        .filter(number => !(importPersonalDataColumnNamesFinalStep[number] === EImportPersonalDataField.PIC_STATUS))
        .map(number => {
            const field = importPersonalDataColumnNamesFinalStep[number];
            return {
                checked: true,
                label: translation[field],
                name: field,
                order: number,
                resizable: true,
                size: importPersonalDataColumnsWidth[field],
                type: 'S',
            } as ITableField<EImportPersonalDataField>
        });
}

export const formImportPersonalDataTableList =
    (tableData: IWizardImportPersonalDataValidatedSubscriberResponse[]) => {
        return  tableData.map(row => {
            const msisdn = formatNumberToPhone(row.msisdn);
            return {
                ...row,
                msisdn: msisdn,
            }
        });
    };

export const formImportPersonalDataTableListFinalStep = (
    tableData: IWizardImportPersonalDataValidatedSubscriberResponse[],
    translation: IImportPersonalDataTableStatusTranslation,
) => {
    return  tableData.map(row => {
        const msisdn = formatNumberToPhone(row.msisdn);
        return {
            ...row,
            docNumber: row.camDocNumber,
            fileDocNumber: row.docNumber,
            fileFullName: [row.surname, row.name, row.patronymic].join(' '),
            fullName:  row.camFullName,
            msisdn: msisdn,
            status: {
                conflicts: row.errors,
                conflictTitle: row.errors?.length > 0 ? translation.conflictTitle : '',
                title: row.errors?.length > 0 ? translation.error : translation.success,
            },
        }
    });
};
